/**
 * TanStack Query hooks for RPC calls.
 *
 * Provides useRpcQuery and useRpcMutation as thin wrappers
 * that use AppRuntime transport for all RPC calls.
 */

import { useQuery, useMutation, type UseQueryOptions, type UseMutationOptions } from "@tanstack/react-query";
import { useAppRuntime } from "../../providers/AppRuntimeProvider";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useRpcQuery<TData>(
  queryKey: readonly unknown[],
  method: string,
  params?: Record<string, unknown>,
  options?: Omit<UseQueryOptions<TData>, "queryKey" | "queryFn">,
) {
  const runtime = useAppRuntime();
  return useQuery<TData>({
    queryKey,
    queryFn: () => runtime.transport.rpc<TData>(method, params),
    ...options,
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useRpcMutation<TData = unknown, TVariables = Record<string, unknown>>(
  method: string,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, "mutationFn">,
) {
  const runtime = useAppRuntime();
  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) =>
      runtime.transport.rpc<TData>(method, variables as Record<string, unknown>),
    ...options,
  });
}
